import Keycloak from "keycloak-js";
import { action, computed, observable } from "mobx";
import { notificationModel } from "../common/component/notifications/notification-model";
import { qzManager } from "../front-office/qz/qz-manager";
import { authenticationService } from "../services/authentication";
import { Permission } from "./permission";
import { ContextProfile } from "./context-profile";
import { Settings } from "./settings";
import { SettingsManager } from "./settings-manager";
import { SettingsProvider } from "./settings-provider";
import { User } from "./user";
import { WarehouseContext } from "./warehouse-context";
import { PalletType } from "../front-office/constants/constants";
import { GetWhoAmIHandler } from "../front-office/query/get-who-am-i/get-who-am-i-handler";
import { Profile } from "../common/model/profile";
import { GetUserProfileHandler } from "../common/cqrs/query/get-user-profile/get-user-profile-handler";
import * as Sentry from "@sentry/react";
import { freshdeskWidgetService } from "../front-office/services/freshdesk-widget";

class Context {

    @observable private _settings: Settings = (new SettingsProvider()).defaultSettings();
    @observable private _currentUser: User = new User();
    @observable private _keykloak: Keycloak.KeycloakInstance<"native">;
    @observable private _warehouseContext: WarehouseContext | undefined;

    private _applicationName: string;
    private readonly _settingsManager = new SettingsManager();

    public qzTimerId: any;

    @computed public get settings() {
        return this._settings;
    }
    @computed public get currentUser() {
        return this._currentUser;
    }
    @computed public get isAuthenticated() {
        return this._currentUser && this._currentUser.token;
    }
    @computed public get isSuperAdministrator() {
        return this._currentUser.isSuperAdministrator;
    }
    @computed public get isAdministrator() {
        return this._currentUser.isAdministrator;
    }
    @computed public get isSupplier() {
        return this._currentUser.isSupplier;
    }
    @computed public get warehouseContext() {
        return this._warehouseContext;
    }
    @computed public get warehouseCode() {
        return this._warehouseContext?.code ?? "";
    }

    @computed public get isImpersonatedUser() {
        return this._currentUser.impersonationToken !== undefined;
    }

    public hasPermission(permission: Permission) {
        return this._currentUser.permissions.has(permission);
    }

    public set applicationName(applicationName: string) {
        this._applicationName = applicationName;
    }

    public get applicationName() {
        return this._applicationName;
    }

    @action
    public setCurrentUser(user: User) {
        this._currentUser = user;

        freshdeskWidgetService.setUserName(user.name);
        freshdeskWidgetService.setUserWarehouse(user.warehouseCode);
    }

    @action
    public initSettings(profile: ContextProfile) {
        this._settings = (new SettingsProvider()).forUser(profile);

        freshdeskWidgetService.setUserEmail(profile.contactEmail);
    }

    @action
    public async updatePrinterType(printerType: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "printerType", printerType));
    }

    @action
    public async updatePrinterPaperFormat(printerPaperFormat: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "printerPaperFormat", printerPaperFormat));
    }

    @action
    public async updateIsDownload(isDownload: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "isDownload", isDownload));
    }

    @action
    public async updatePrinterName(printerName: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "printerName", printerName));
    }

    @action
    public async updateLocale(locale: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "lang", locale));
    }

    @action
    public async updateMute(muted: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "muted", muted));
    }

    @action
    public async updateEmail(email: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "contactEmail", email));

        return context.settings.contactEmail === email;
    }

    @action
    public async updateWebhookUrl(webhookUrl: string) {
        this.resetSettings(await this._settingsManager.update(context.settings, "webhookUrl", webhookUrl));

        return context.settings.webhookUrl === webhookUrl;
    }

    @action
    public async updatePalletType(palletType: PalletType) {
        this.resetSettings(await this._settingsManager.update(context.settings, "palletType", palletType));

        return context.settings.palletType === palletType;
    }

    @action
    private resetSettings(settings: Settings) {
        this._settings = settings;
    }

    @action
    public updateCurrentUserSpareToken(token: string) {
        this._currentUser.spareToken = token;
    }

    @action
    public updateCurrentUserSsoToken(ssoToken: string) {
        this._currentUser.ssoToken = ssoToken;
    }

    @action
    public switchCurrentUserTokens() {
        this._currentUser.useSpareToken();
        localStorage.setItem("ds_user_session", JSON.stringify(this.currentUser));
    }

    public async initSso() {
        if (!window.__CONFIG__.SSO) {
            return;
        }

        this._keykloak = new Keycloak({
            url: window.__CONFIG__.SSO.Url,
            realm: window.__CONFIG__.SSO.Realm,
            clientId: window.__CONFIG__.SSO.Resource,
        });

        return await this._keykloak.init({
            onLoad: this._currentUser.authScheme === "sso" ? "check-sso" : undefined,
            checkLoginIframe: false,
        }).then(async (authenticated: any) => {
            if (authenticated && this._keykloak.token) {
                const userName = (this._keykloak.tokenParsed as any).preferred_username;
                try {
                    const tokenResponse = await authenticationService.tokenForSsoUser(this._keykloak.token);
                    const newUser = new User(userName, tokenResponse.access_token, this._keykloak.token, "sso");
                    this.setCurrentUser(newUser);
                    await GetWhoAmIHandler();
                    this.saveCurrentUserInLocalStorage();
                } catch (e) {
                    notificationModel.addErrorMessage("error.authentication.userNotRegistered");
                    throw new Error("User not registered");
                }
            }
        })
        .catch(() => this.reset());
    }

    public initUserSession() {
        const storedSession = localStorage.getItem("ds_user_session");
        const impersonationToken = sessionStorage.getItem("ds_impersonation_token");

        if (storedSession) {
            const user = User.unserialize(storedSession);
            user.impersonationToken = impersonationToken ?? undefined;
            this.setCurrentUser(user);
        }

        Sentry.setUser({ name: this._currentUser.name, is_supervised: impersonationToken !== null });

        return null;
    }

    public async initAuthenticatedContextAsync(firstLogin: boolean = false, profile?: Profile) {
        if (!this.isAuthenticated) {
            return;
        }
        try {
            this.initSettings(ContextProfile.fromModel(profile ?? await GetUserProfileHandler()));
            this.initializeWarehouseContext();
            Sentry.setContext("feature_flags", {
                flags: Object.fromEntries(Array.from(this._currentUser.permissions.values()).map(f => [f, true])),
            });

            qzManager.qzHealthTimer();

            // Get spare token to automatic renewal
            if (firstLogin) {
                const response = await authenticationService.token();
                this.updateCurrentUserSpareToken(response.access_token);
            }

            this.saveCurrentUserInLocalStorage();
        } catch (e) {
            this.reset();
        }
    }

    @action
    private initializeWarehouseContext() {
        this._warehouseContext = WarehouseContext.initializeForUser(this.currentUser);
    }

    @action
    public updateAdministratorWarehouseContext(warehouse?: string, country?: string) {
        if (warehouse === undefined) {
            this._warehouseContext = undefined;

            return;
        }

        if (!this._currentUser.isSuperAdministrator) {
            throw new Error("Permission denied");
        }

        this._warehouseContext = new WarehouseContext(warehouse, country ?? "UNKNOWN");
    }

    public saveImpersonationTokenInSessionStorage(impersonationToken: string): void {
        sessionStorage.setItem("ds_impersonation_token", impersonationToken);
    }

    private saveCurrentUserInLocalStorage(): void {
        localStorage.setItem("ds_user_session", this._currentUser.serialize());
    }

    @action
    public clearImpersonation() {
        sessionStorage.removeItem("ds_impersonation_token");
    }

    @action
    public reset() {
        localStorage.removeItem("ds_user_session");
        this.clearImpersonation();
        this._currentUser =  new User();
        clearInterval(this.qzTimerId);
    }

    public async refreshSsoTokenIfSsoEnabled(): Promise<void> {
        if (!this.isSsoActive() || this._currentUser.authScheme !== "sso") {
            return Promise.resolve();
        }

        const ssoToken = await new Promise<string | undefined>(resolve => {
            this._keykloak.updateToken(5)
                .then((refreshed: boolean) => {
                if (refreshed && this._keykloak.token) {
                    this.updateCurrentUserSsoToken(this._keykloak.token);
                    resolve(this._keykloak.token);
                }
                resolve(undefined);
            }).catch(resolve);
        });

        if (ssoToken) {
            const tokenResponse = await authenticationService.tokenForSsoUser(ssoToken);
            this.updateCurrentUserSpareToken(tokenResponse.access_token);
            this.switchCurrentUserTokens();
        }
    }

    public async getSsoLoginUrl(redirectPath: string) {
        return await this._keykloak.createLoginUrl({
            redirectUri: window.location.origin + redirectPath,
        });
    }

    public isSsoActive(): boolean {
        return this._keykloak !== undefined;
    }
}

export const context = new Context();
